import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_864cc152-5d80-4928-acac-3bcce367b776/artifacts/dhju5nz8_Grassroots%20Coach%20Logo%20260329.png";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const theme = localStorage.getItem('landingTheme') || 'dark';

  useEffect(() => {
    const verifyToken = async () => {
      try {
        await axios.get(`${API}/api/auth/verify-reset-token/${token}`);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="auth-container" data-theme={theme}>
        <div className="w-full max-w-md px-4">
          <Card className="auth-card border-0 shadow-2xl animate-fadeIn">
            <CardContent className="py-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Verifying reset link...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-container" data-theme={theme}>
        <div className="w-full max-w-md px-4">
          <Card className="auth-card border-0 shadow-2xl animate-fadeIn">
            <CardHeader className="text-center space-y-4 pb-8">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-lg">
                <XCircle className="w-10 h-10 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <CardTitle className="text-3xl font-bold text-gray-900 font-title tracking-tight">
                  Invalid Link
                </CardTitle>
                <CardDescription className="text-gray-500 mt-2">
                  This password reset link is invalid or has expired.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="text-center">
              <Link 
                to="/forgot-password" 
                className="font-semibold"
              >
                Request a new reset link
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container" data-theme={theme}>
        <div className="w-full max-w-md px-4">
          <Card className="auth-card border-0 shadow-2xl animate-fadeIn">
            <CardHeader className="text-center space-y-4 pb-8">
              <div className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center">
                <img src={LOGO_URL} alt="Logo" className="w-20 h-20 object-contain" />
              </div>
              <div>
                <CardTitle className="text-3xl font-bold text-gray-900 font-title tracking-tight">
                  Password Reset!
                </CardTitle>
                <CardDescription className="text-gray-500 mt-2">
                  Your password has been successfully reset.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="text-center">
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-12 rounded-xl text-white font-semibold shadow-lg btn-auth btn-transition"
              >
                Sign In Now
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container" data-theme={theme}>
      <div className="w-full max-w-md px-4">
        <Card className="auth-card border-0 shadow-2xl animate-fadeIn">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center">
              <img src={LOGO_URL} alt="Logo" className="w-20 h-20 object-contain" />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-gray-900 font-title tracking-tight">
                Reset Password
              </CardTitle>
              <CardDescription className="text-gray-500 mt-2">
                Enter your new password below
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-medium">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" strokeWidth={1.5} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" strokeWidth={1.5} />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 rounded-xl border-gray-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    data-testid="reset-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl text-white font-semibold shadow-lg btn-auth btn-transition"
                data-testid="reset-submit-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
